import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "@/App.css";
import SEO from "../components/shared/SEO";
import { 
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Users,
  Target,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Award,
  Search,
  GraduationCap,
  Calendar,
  Lightbulb,
  XCircle,
  Star,
  BarChart3,
  ChevronUp
} from "lucide-react";

// Brand Colors
const BRAND = {
  gold: "#D4AF37",
  goldLight: "#E8C547",
  charcoal: "#1C1C1C",
  surface: "#0A0A0A",
  text: "#FAFAFA"
};

// Section Data
const SECTIONS = [
  {
    id: "intro",
    title: "Introduction",
    shortTitle: "Intro",
    icon: BookOpen,
    content: [
      {
        type: "lead",
        text: "Most companies treat SDR as a temporary role. High-performing teams treat it as the foundation of a revenue system."
      },
      {
        type: "warning",
        title: "The Common Approach Creates",
        items: ["High turnover", "Inconsistent pipeline", "Constant rehiring cycles"]
      },
      {
        type: "insight",
        title: "The Better Way",
        text: "When structured correctly, the SDR role becomes the foundation for both pipeline generation and future sales leadership."
      }
    ]
  },
  {
    id: "concept",
    title: "The Concept",
    shortTitle: "Concept",
    icon: Target,
    content: [
      {
        type: "lead",
        text: "A revenue talent system is a structured approach to developing SDRs into higher-performing roles over time."
      },
      {
        type: "grid3",
        items: [
          { title: "Identify", desc: "high-potential SDRs", icon: "Search" },
          { title: "Develop", desc: "real selling skills", icon: "GraduationCap" },
          { title: "Prepare", desc: "them for closing roles", icon: "Award" }
        ]
      },
      {
        type: "insight",
        title: "The Result",
        text: "This creates a more consistent and scalable revenue organization—reducing dependency on external hiring."
      }
    ]
  },
  {
    id: "experience",
    title: "From Experience",
    shortTitle: "Experience",
    icon: Lightbulb,
    content: [
      {
        type: "lead",
        text: "In teams I've worked with, SDRs often became the strongest future AEs—outperforming external hires consistently."
      },
      {
        type: "checklist",
        items: [
          "SDRs often became the strongest future AEs",
          "Internal promotions outperformed external hires",
          "Pipeline quality improved as SDR capability increased"
        ]
      }
    ]
  },
  {
    id: "framework",
    title: "The Framework",
    shortTitle: "Framework",
    icon: Users,
    content: [
      {
        type: "lead",
        text: "A four-stage development system that transforms SDR performance into long-term revenue capability."
      },
      {
        type: "workflow",
        steps: [
          { label: "Identify", sub: "Spot Potential", icon: "Search", color: "blue" },
          { label: "Train", sub: "Build Skills", icon: "GraduationCap", color: "purple" },
          { label: "Develop", sub: "Real Experience", icon: "Zap", color: "gold" },
          { label: "Promote", sub: "AE Ready", icon: "Award", color: "green" }
        ]
      },
      {
        type: "numbered",
        items: [
          { title: "Identify", desc: "Look for consistency under pressure, curiosity during conversations, and resilience after rejection. The best future AEs aren't always the loudest—they're the most coachable." },
          { title: "Train", desc: "Develop real skills: messaging, discovery, objection handling—not just activity volume." },
          { title: "Develop", desc: "Expose SDRs to real deal cycles—shadowing calls, writing follow-ups for active opportunities, joining pipeline reviews." },
          { title: "Promote", desc: "Move top performers into AE roles when they are truly ready—not just when a seat opens." }
        ]
      }
    ]
  },
  {
    id: "apply",
    title: "How to Apply",
    shortTitle: "Apply",
    icon: Zap,
    content: [
      {
        type: "lead",
        text: "Start small with 1-2 high-potential SDRs. Build systems that can scale as your team grows."
      },
      {
        type: "list",
        title: "Invest In",
        items: [
          "Coaching and structured feedback",
          "Exposure to real deals",
          "Skill development frameworks"
        ]
      },
      {
        type: "insight",
        title: "The Goal",
        text: "Over time, build a repeatable system that turns SDR performance into long-term revenue capability."
      }
    ]
  },
  {
    id: "mistakes",
    title: "Common Mistakes",
    shortTitle: "Mistakes",
    icon: AlertTriangle,
    content: [
      {
        type: "lead",
        text: "These patterns consistently undermine SDR talent development—recognize them early."
      },
      {
        type: "mistakes",
        items: [
          "Treating SDR as a short-term role",
          "Promoting too early without real skill development",
          "Focusing only on activity instead of capability",
          "Relying entirely on external hiring"
        ]
      }
    ]
  },
  {
    id: "outcomes",
    title: "Outcomes",
    shortTitle: "Outcomes",
    icon: TrendingUp,
    content: [
      {
        type: "lead",
        text: "When implemented well, this approach transforms pipeline consistency and reduces hiring risk."
      },
      {
        type: "grid2",
        items: [
          { title: "Pipeline Consistency", icon: "BarChart3" },
          { title: "Higher Conversion", icon: "TrendingUp" },
          { title: "Reduced Hiring Risk", icon: "Users" },
          { title: "Stronger Teams", icon: "Star" }
        ]
      },
      {
        type: "progression",
        steps: ["SDR", "Senior SDR", "AE", "Top Performer"]
      }
    ]
  }
];

// Header Component
const Header = () => {
  const navigate = useNavigate();
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <svg width="24" height="24" viewBox="0 0 64 64" fill="none">
              <rect x="6" y="46" width="11" height="14" fill={BRAND.gold} opacity="0.5" rx="2"/>
              <rect x="20" y="36" width="11" height="24" fill={BRAND.gold} opacity="0.65" rx="2"/>
              <rect x="34" y="24" width="11" height="36" fill={BRAND.gold} opacity="0.8" rx="2"/>
              <rect x="48" y="10" width="11" height="50" fill={BRAND.gold} rx="2"/>
            </svg>
            <span className="text-sm font-bold text-white">LRS</span>
          </button>
          
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
        </div>
      </div>
    </header>
  );
};

// Sticky Section Navigation
const StickyNav = ({ sections, activeSection }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="sticky top-14 sm:top-16 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
          {sections.map((section, i) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap transition-all text-xs font-medium ${
                  isActive 
                    ? 'bg-[#D4AF37] text-[#0A0A0A]' 
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{section.shortTitle}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Content Block Components
const LeadBlock = ({ text }) => (
  <p className="text-lg sm:text-xl text-zinc-200 leading-relaxed font-medium">
    {text}
  </p>
);

const WarningBlock = ({ title, items }) => (
  <div className="p-4 sm:p-5 bg-red-500/10 border-l-4 border-red-500 rounded-r-xl">
    <h4 className="text-base font-semibold text-white mb-3">{title}</h4>
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-center gap-3 text-zinc-300 text-sm">
          <div className="w-2 h-2 rounded-full bg-red-400" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const InsightBlock = ({ title, text }) => (
  <div className="p-4 sm:p-5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
    <div className="flex items-start gap-3">
      <Lightbulb className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
      <div>
        <h4 className="font-semibold text-white mb-1">{title}</h4>
        <p className="text-sm text-zinc-300">{text}</p>
      </div>
    </div>
  </div>
);

const Grid3Block = ({ items }) => {
  const iconMap = { Search, GraduationCap, Award };
  
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {items.map((item) => {
        const Icon = iconMap[item.icon];
        return (
          <div key={item.title} className="p-4 bg-[#1C1C1C] rounded-xl border border-white/5">
            <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
            <p className="text-white text-sm font-medium">{item.title}</p>
            <p className="text-zinc-400 text-xs mt-1">{item.desc}</p>
          </div>
        );
      })}
    </div>
  );
};

const Grid2Block = ({ items }) => {
  const iconMap = { BarChart3, TrendingUp, Users, Star };
  
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {items.map((item) => {
        const Icon = iconMap[item.icon];
        return (
          <div key={item.title} className="p-4 bg-[#1C1C1C] rounded-xl border border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <span className="text-white font-medium text-sm">{item.title}</span>
          </div>
        );
      })}
    </div>
  );
};

const ChecklistBlock = ({ items }) => (
  <div className="space-y-2">
    {items.map((item) => (
      <div key={item} className="flex items-center gap-3 p-3 bg-[#1C1C1C] rounded-xl">
        <CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
        <span className="text-sm text-zinc-300">{item}</span>
      </div>
    ))}
  </div>
);

const WorkflowBlock = ({ steps }) => {
  const iconMap = { Search, GraduationCap, Zap, Award };
  const colorMap = {
    blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    gold: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30",
    green: "bg-green-500/20 text-green-400 border-green-500/30"
  };
  
  return (
    <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
      <div className="flex gap-2 sm:gap-3 min-w-max pb-2">
        {steps.map((step, i) => {
          const Icon = iconMap[step.icon];
          return (
            <div key={step.label} className="flex items-center">
              <div className={`p-3 sm:p-4 rounded-xl ${colorMap[step.color]} border min-w-[100px] sm:min-w-[120px] text-center`}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2" />
                <div className="font-semibold text-white text-xs sm:text-sm">{step.label}</div>
                <div className="text-[10px] sm:text-xs text-zinc-400 mt-1">{step.sub}</div>
              </div>
              {i < steps.length - 1 && (
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-600 mx-1" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const NumberedBlock = ({ items }) => (
  <div className="space-y-3">
    {items.map((item, i) => (
      <div key={item.title} className="flex gap-3 p-3 sm:p-4 bg-[#1C1C1C] rounded-xl border border-white/5">
        <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-[#0A0A0A]">{i + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white text-sm sm:text-base">{item.title}</h4>
          <p className="text-xs sm:text-sm text-zinc-400 mt-0.5">{item.desc}</p>
        </div>
      </div>
    ))}
  </div>
);

const ListBlock = ({ title, items }) => (
  <div className="p-4 bg-[#1C1C1C] rounded-xl border border-white/5">
    <p className="text-white font-medium mb-3">{title}</p>
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-center gap-3 text-zinc-300 text-sm">
          <CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const MistakesBlock = ({ items }) => (
  <div className="space-y-3">
    {items.map((mistake) => (
      <div key={mistake} className="p-3 bg-[#1C1C1C] rounded-xl border border-white/5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
          <XCircle className="w-4 h-4 text-red-400" />
        </div>
        <span className="text-zinc-300 text-sm">{mistake}</span>
      </div>
    ))}
  </div>
);

const ProgressionBlock = ({ steps }) => (
  <div className="p-5 bg-[#1C1C1C] rounded-2xl border border-white/5">
    <p className="text-xs text-zinc-500 text-center mb-4">The talent progression path</p>
    <div className="flex flex-wrap items-center justify-center gap-2">
      {steps.map((step, i) => (
        <div key={step} className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-lg border text-sm ${
            i === steps.length - 1 
              ? 'bg-[#D4AF37]/20 border-[#D4AF37]/40 text-[#D4AF37] font-medium'
              : i === steps.length - 2
              ? 'bg-[#0A0A0A] border-[#D4AF37]/30 text-white font-medium'
              : 'bg-[#0A0A0A] border-white/10 text-zinc-400'
          }`}>
            {step}
          </div>
          {i < steps.length - 1 && (
            <ArrowRight className="w-4 h-4 text-[#D4AF37]/50" />
          )}
        </div>
      ))}
    </div>
  </div>
);

const CTABlock = () => (
  <div className="p-6 bg-gradient-to-br from-[#D4AF37]/20 to-[#1C1C1C] rounded-2xl border border-[#D4AF37]/30 text-center">
    <h4 className="text-lg font-semibold text-white mb-2">Ready to Build Your System?</h4>
    <p className="text-sm text-zinc-300 mb-4">Strong revenue teams don't rely on hiring alone. They build systems that develop talent over time.</p>
    <a 
      href="https://calendly.com/leitner-revenue-systems/30min"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold text-sm hover:bg-[#E8C547] transition-all"
    >
      <Calendar className="w-4 h-4" />
      Book a Strategy Call
    </a>
  </div>
);

// Section Component
const Section = ({ section, index, isVisible }) => {
  const Icon = section.icon;
  
  const renderContent = (content, idx) => {
    switch (content.type) {
      case 'lead': return <LeadBlock key={idx} text={content.text} />;
      case 'warning': return <WarningBlock key={idx} title={content.title} items={content.items} />;
      case 'insight': return <InsightBlock key={idx} title={content.title} text={content.text} />;
      case 'grid3': return <Grid3Block key={idx} items={content.items} />;
      case 'grid2': return <Grid2Block key={idx} items={content.items} />;
      case 'checklist': return <ChecklistBlock key={idx} items={content.items} />;
      case 'workflow': return <WorkflowBlock key={idx} steps={content.steps} />;
      case 'numbered': return <NumberedBlock key={idx} items={content.items} />;
      case 'list': return <ListBlock key={idx} title={content.title} items={content.items} />;
      case 'mistakes': return <MistakesBlock key={idx} items={content.items} />;
      case 'progression': return <ProgressionBlock key={idx} steps={content.steps} />;
      case 'cta': return <CTABlock key={idx} />;
      default: return null;
    }
  };

  return (
    <section 
      id={section.id}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#D4AF37]/20">
          <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#0A0A0A]" />
        </div>
        <div>
          <span className="text-xs font-medium text-[#D4AF37]">Section {index + 1}</span>
          <h2 className="text-xl sm:text-2xl font-bold text-white">{section.title}</h2>
        </div>
      </div>

      <div className="space-y-5 pl-0 sm:pl-[72px]">
        {section.content.map((content, idx) => renderContent(content, idx))}
      </div>

      <div className="mt-10 mb-10 border-t border-white/5" />
    </section>
  );
};

// Back to Top Button
const BackToTop = ({ show }) => (
  <button
    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#D4AF37] text-[#0A0A0A] shadow-lg shadow-[#D4AF37]/30 flex items-center justify-center transition-all duration-300 ${
      show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
    }`}
  >
    <ChevronUp className="w-6 h-6" />
  </button>
);

// Main Component
function TalentPoolPage() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);

      const scrollPosition = window.scrollY + 150;
      
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const section = SECTIONS[i];
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // SECTIONS is a static constant defined outside component
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] overflow-x-hidden">
      <SEO 
        title="Building Talent Pools | High-Growth Sales Team Guide"
        description="Build a revenue talent system from your SDR team. A practical guide to developing SDRs into high-performing AEs and building a consistent pipeline engine."
        path="/resources/talent-pool"
        image="talent"
      />
      <Header />
      <StickyNav sections={SECTIONS} activeSection={activeSection} />
      
      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-8 sm:pb-10 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-transparent" />
          <img 
            src="https://media.licdn.com/dms/image/v2/D5612AQGWYSSnqjD04Q/article-cover_image-shrink_720_1280/B56Zza7McgH8AI-/0/1773199482538?e=2147483647&v=beta&t=EdThB6PbZGoiEiUeYzUNigz3957oIopIj_ofVkBKV7k"
            alt="Talent System"
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/70" />
        </div>
        
        <div className="relative max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-medium text-[#D4AF37]">LRS Playbook</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-4">
            Build a Revenue Talent System from Your{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#E8C547]">
              SDR Team
            </span>
          </h1>
          
          <p className="text-base sm:text-lg text-zinc-300 mb-6 max-w-2xl">
            A practical guide to developing SDRs into high-performing AEs and building a consistent pipeline engine.
          </p>
          
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_efficiency-boost-10/artifacts/tvktt6ia_IMG_0009.jpeg"
              alt="Dan Leitner"
              className="w-12 h-12 rounded-full object-cover border-2 border-[#D4AF37]/30"
            />
            <div>
              <div className="font-medium text-white">Dan Leitner</div>
              <div className="text-sm text-[#D4AF37]">Founder & Principal Consultant</div>
            </div>
          </div>
        </div>
      </section>

      {/* All Sections */}
      <div className="px-4 sm:px-6 lg:px-12 pb-20">
        <div className="max-w-4xl mx-auto">
          {SECTIONS.map((section, index) => (
            <Section
              key={section.id}
              section={section}
              index={index}
              isVisible={isLoaded}
            />
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="px-4 sm:px-6 pb-20">
        <div className="max-w-4xl mx-auto p-8 bg-gradient-to-br from-[#D4AF37]/20 to-[#1C1C1C] rounded-2xl border border-[#D4AF37]/30 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Ready to Build Your System?</h3>
          <p className="text-zinc-300 mb-6 max-w-md mx-auto">
            Let's discuss how to design a talent development system for your specific revenue challenges.
          </p>
          <a 
            href="https://calendly.com/leitner-revenue-systems/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold hover:bg-[#E8C547] transition-all shadow-lg shadow-[#D4AF37]/20"
          >
            <Calendar className="w-5 h-5" />
            Book a Strategy Call
          </a>
        </div>
      </div>

      <BackToTop show={showBackToTop} />
    </div>
  );
}

export default TalentPoolPage;
